/**
 * Postcode look-up for address questions (23/10/2026): the applicant types a
 * UK postcode, picks their address, and its lines fill in. The intake Worker
 * asks JFH's address lookup Worker (Ideal Postcodes, the same one the iQ
 * address plug-in uses) over a service binding; the key never leaves it.
 */

import type { AddressComponent, AddressConfig } from './fields.js';
import { resolveAddressComponents } from './fields.js';

/** One address at a postcode, as `GET /api/public/postcodes/:postcode` returns it. */
export interface PostcodeAddress {
  organisation: string;
  line1: string;
  line2: string;
  line3: string;
  town: string;
  county: string;
  postcode: string;
}

/** What a look-up came back with (the form falls back to typing on anything but `found`). */
export type PostcodeLookupOutcome =
  | { kind: 'found'; postcode: string; addresses: PostcodeAddress[] }
  | { kind: 'not_found' }
  | { kind: 'invalid' }
  | { kind: 'busy' }
  | { kind: 'unavailable' };

const UK_POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/;

/** "ip51ab" → "IP5 1AB"; null when it isn't a whole UK postcode. */
export function formatUkPostcode(raw: unknown): string | null {
  const compact = String(raw ?? '').toUpperCase().replace(/\s+/g, '');
  return UK_POSTCODE.test(compact) ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : null;
}

/**
 * Whether an address question offers the look-up: on unless switched off,
 * and only when it asks for a postcode.
 */
export function offersPostcodeLookup(address: AddressConfig | undefined): boolean {
  if (address?.postcodeLookup === false) return false;
  return resolveAddressComponents({ address }).some((c) => c.key === 'postcode' && c.enabled);
}

/**
 * The organisation and street lines, without repeating the organisation:
 * Ideal Postcodes often already starts line 1 with it ("Buckingham Palace").
 */
function streetLines(a: PostcodeAddress): string[] {
  const lines = [a.line1, a.line2, a.line3].map((s) => s.trim()).filter(Boolean);
  const org = a.organisation.trim();
  const repeated = !org || lines.some((l) => l.toLowerCase() === org.toLowerCase());
  return repeated ? lines : [org, ...lines];
}

/**
 * The street lines to fill in, without the business name: Ideal Postcodes
 * puts the organisation in line 1 for businesses, and the company name is
 * asked separately (Hayden, 23/10/2026). Kept only when it is the whole
 * address ("Buckingham Palace"), so the lines aren't left empty.
 */
function fillLines(a: PostcodeAddress): string[] {
  const lines = [a.line1, a.line2, a.line3].map((s) => s.trim()).filter(Boolean);
  const org = a.organisation.trim().toLowerCase();
  const street = org ? lines.filter((l) => l.toLowerCase() !== org) : lines;
  return street.length ? street : lines;
}

/** One line to show in the "Choose your address" list (with the business name, to help pick). */
export function postcodeAddressLabel(a: PostcodeAddress): string {
  return [...streetLines(a), a.town.trim()].filter(Boolean).join(', ');
}

/**
 * The address lines a picked address fills, for an address question's own
 * lines: the street lines (not the business name) go into the address lines
 * it asks for (overflow joins the last one), town → city, county, postcode.
 * The country becomes GB when the question asks for it.
 */
export function postcodeAddressValue(
  a: PostcodeAddress,
  address: AddressConfig | undefined,
): Partial<Record<AddressComponent, string>> {
  const on = new Set(resolveAddressComponents({ address }).filter((c) => c.enabled).map((c) => c.key));
  const street = fillLines(a);
  const slots = (['line1', 'line2', 'line3'] as const).filter((k) => on.has(k));
  const out: Partial<Record<AddressComponent, string>> = { line1: '', line2: '', line3: '' };
  if (slots.length) {
    slots.forEach((k, i) => {
      out[k] = i === slots.length - 1 ? street.slice(i).join(', ') : (street[i] ?? '');
    });
  }
  out.city = a.town.trim();
  out.county = on.has('county') ? a.county.trim() : '';
  out.postcode = a.postcode.trim();
  if (on.has('country')) out.country = 'GB';
  return out;
}
